import { groupBy } from "lodash";
import React from "react";
import {
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from "@material-ui/core";
import { fields } from "../fields";
import { domainCorrections, Problem as ProblemType } from "../validate";
import styles from "./validations.module.css";

// Helper function to get appropriate error message for different validation types
const getErrorMessageText = (problem: ProblemType): string => {
  const { key, data } = problem;

  switch (true) {
    case key === "invalid-date-format":
      return " Invalid date format ";

    case key === "position-order":
      return " contains an invalid position order of ";

    case key === "submit-date-order":
      return "Contains an unordered value";

    case key === "missing-contact-info":
      return " Missing contact information ";

    case key.startsWith("invalid-") && key.includes("email"):
      return " Invalid email format ";

    case key.startsWith("typo-") && key.includes("email"): {
      // Extract domain from email
      const email = "value" in data ? data.value : "";
      const domain = email.split("@")[1]?.toLowerCase();
      const correction = domain && domainCorrections[domain as keyof typeof domainCorrections];
      return correction ? ` Did you mean ${correction}? ` : " Email domain may contain a typo";
    }

    case key.startsWith("invalid-") && key.includes("phone"):
      return " Invalid phone format ";

    case key === "invalid-status-combination":
      return " Applicant cannot be moved in and denied at the same time! ";

    default:
      return "Contains an invalid value";
  }
};

const warnings: Record<string, string> = {
  "missing-contact-info": "No contact info",
  "missing-time": "Missing time, will be set to 12:00 AM GMT",
  "missing-date-time": "Missing date and time",
};
const warningKeys = Object.keys(warnings) as Array<keyof typeof warnings>;

export const renderProblemText = (problem: ProblemType): string => {
  const { key, type, data } = problem;

  if (warningKeys.includes(key)) {
    return warnings[key];
  }

  switch (type) {
    case "global":
      return data.message;

    case "default":
      return `Will be converted from ${data.value} to ${data.default}`;

    case "record": {
      if (!(data.field in fields)) return "";
      return getErrorMessageText(problem);
    }

    default:
      return "";
  }
};

const MissingDataWarning: React.FC<{dataKey: keyof typeof warnings}> = ({ dataKey }) => {
  const message = warnings[dataKey];
  if (!message) return null;
  return <span>{message}</span>;
};

export const Problem: React.FC<{ problem: ProblemType }> = ({ problem }) => {
  const { key, type, data } = problem;

  if (warningKeys.includes(key)) {
    return <MissingDataWarning dataKey={key} />;
  }

  switch (type) {
    case "global":
      return <span>{data.message}</span>;

    case "default":
      return (
        <span>
          <span className={styles.warning}>{data.field}</span> will be converted from{" "}
          <span className={styles.data}>{data.value}</span> to{" "}
          <span className={styles.data}>{data.default}</span>
        </span>
      );

    case "record": {
      if (!(data.field in fields)) return null;
      return (<span>{getErrorMessageText(problem)}</span>);
    }

    default:
      return null;
  }
};


const ValidationLineItem: React.FC<{ problems: ProblemType[] }> = ({ problems }) => {
  const [seeAll, setSeeAll] = React.useState(false);
  const [candidate, ...tail] = problems;
  if (!candidate) return null;

  return (
    <>
      <TableRow>
        <TableCell>
          <Problem problem={candidate} />
          {problems.length > 1 && (
            <Button variant="text" color="primary" onClick={() => setSeeAll(!seeAll)}>
              {seeAll ? "Hide all" : "Show all"}
            </Button>
          )}
        </TableCell>
        <TableCell>Occurrences: {problems.length}</TableCell>
      </TableRow>
      {seeAll &&
        tail.map(problem => (
          <TableRow key={problem.key}>
            <TableCell>
              <Problem problem={problem} />
            </TableCell>
            <TableCell />
          </TableRow>
        ))}
    </>
  );
};

const Validations: React.FC<{ problems: ProblemType[] }> = ({ problems }) => {
  const grouped = groupBy(problems, problem => problem.key);

  return (
    <TableContainer component={Paper}>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>Problem</TableCell>
            <TableCell>Instances</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {Object.entries(grouped).map(([key, group]) => {
            const [candidate] = group;
            if (!candidate) return null;
            return <ValidationLineItem problems={group} key={key} />;
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default Validations;
